import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  createRootRouteWithContext,
  HeadContent,
  Link,
  Outlet,
  Scripts,
  useRouter,
} from "@tanstack/react-router";
import { useEffect } from "react";
import { MotionConfig } from "framer-motion";
import { ThemeProvider } from "next-themes";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { reportVibeError } from "@/lib/vibe-error-reporting";
import { videoObjectSchema } from "@/lib/seo";
import appCss from "../styles.css?url";

const siteTitle = "RevOps Architect & AI Automation Expert | Marjohn Robillo";
const siteDescription =
  "300+ CRM systems architected. Hire an elite Revenue Operations & AI automation architect from the Philippines: n8n, agentic voice AI, and RevOps engineering.";

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en" suppressHydrationWarning>
      <head>
        <HeadContent />
        {/*
          SEO / AEO safety net: no-JS crawlers and AI retrieval bots
          (GPTBot, ClaudeBot, PerplexityBot, OAI-SearchBot) often do not
          execute the Framer Motion scroll-reveal animations, leaving
          below-fold content styled at opacity:0. This forces all animated
          content visible when JavaScript is unavailable so crawlers and
          LLMs index the full page text.
        */}
        <noscript>
          <style>{`*[style*="opacity: 0"], *[style*="opacity:0"]{opacity:1 !important;transform:none !important}`}</style>
        </noscript>
      </head>
      <body className="bg-white text-foreground font-sans antialiased overflow-x-hidden selection:bg-primary/30">
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();

  return (
    <ThemeProvider
      attribute="class"
      defaultTheme="dark"
      enableSystem={false}
      disableTransitionOnChange
    >
      <QueryClientProvider client={queryClient}>
        <MotionConfig reducedMotion="user">
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <Outlet />
          </TooltipProvider>
        </MotionConfig>
      </QueryClientProvider>
    </ThemeProvider>
  );
}

function RootErrorComponent({
  error,
  reset,
}: {
  error: Error;
  reset: () => void;
}) {
  const router = useRouter();

  useEffect(() => {
    console.error("RootErrorComponent caught:", error);
    try {
      reportVibeError(error, { boundary: "tanstack_root_error_component" });
    } catch (e) {
      console.error("Failed to report vibe error:", e);
    }
  }, [error]);

  const retry = () => {
    router.invalidate();
    reset();
  };

  return (
    <main className="flex min-h-screen items-center justify-center bg-background px-6 text-foreground">
      <section className="max-w-lg text-center">
        <p className="font-space text-sm font-semibold uppercase tracking-[0.2em] text-primary">
          Unexpected error
        </p>
        <h1 className="mt-3 font-space text-4xl font-bold">
          This page didn&apos;t load
        </h1>
        <p className="mt-4 text-muted-foreground">
          Please try again, or return to the home page.
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <button
            onClick={retry}
            className="rounded-lg bg-primary px-5 py-3 font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <Link
            to="/"
            className="rounded-lg border border-border bg-card px-5 py-3 font-medium text-foreground transition-colors hover:bg-muted"
          >
            Go home
          </Link>
        </div>
      </section>
    </main>
  );
}

function RootNotFoundComponent() {
  return (
    <main className="flex min-h-screen items-center justify-center bg-background px-6 text-foreground">
      <section className="max-w-lg text-center">
        <p className="font-space text-8xl font-extrabold text-primary/20">
          404
        </p>
        <h1 className="mt-4 font-space text-3xl font-bold">
          This page doesn&apos;t exist
        </h1>
        <p className="mt-3 text-muted-foreground">
          The page you&apos;re looking for may have moved or been removed.
        </p>
        <Link
          to="/"
          className="mt-8 inline-flex rounded-lg bg-primary px-5 py-3 font-medium text-primary-foreground transition-colors hover:bg-primary/90"
        >
          Back to Home
        </Link>
      </section>
    </main>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()(
  {
    // @ts-expect-error root head links include HTML passthrough attributes (media, onLoad, fetchPriority) not modelled by TanStack's strict head type
    head: () => ({
      meta: [
        { charSet: "utf-8" },
        { name: "viewport", content: "width=device-width, initial-scale=1" },
        { title: siteTitle },
        { name: "description", content: siteDescription },
        {
          name: "keywords",
          content:
            "revenue operations architect, RevOps specialist, CRM automation, n8n workflow developer, voice AI, Vapi automation, technical virtual assistant, Philippines automation engineer, Marjohn Robillo, Zappify",
        },
        { name: "author", content: "Marjohn B. Robillo" },
        {
          name: "bingbot",
          content:
            "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1",
        },
        { property: "og:title", content: siteTitle },
        { property: "og:description", content: siteDescription },
        { property: "og:type", content: "website" },
        {
          property: "og:image",
          content:
            "https://assets.cdn.filesafe.space/pLFrnXnIu7IgQ4WCaCfz/media/6ac3f6752c1c51a2ac7081a0.png",
        },
        { property: "og:url", content: "https://marjohnrobillo.me/" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: siteTitle },
        { name: "twitter:description", content: siteDescription },
        {
          name: "twitter:image",
          content:
            "https://assets.cdn.filesafe.space/pLFrnXnIu7IgQ4WCaCfz/media/6ac3f6752c1c51a2ac7081a0.png",
        },
      ],
      links: [
        { rel: "stylesheet", href: appCss },
        {
          rel: "stylesheet",
          href: "https://fonts.googleapis.com/css2?family=Caveat:wght@500;600&family=Inter+Tight:wght@700;800;900&family=Inter:wght@400;500;600;700&family=Space+Grotesk:wght@500;700;800&display=swap",
          media: "print",
          onLoad: "this.media='all'",
        },
        {
          rel: "icon",
          type: "image/png",
          href: "https://vibe.filesafe.space/meta/1790177007883282700/favicon.webp",
        },
        {
          rel: "alternate",
          type: "application/rss+xml",
          title: "Marjohn B. Robillo - Blog",
          href: "https://marjohnrobillo.me/rss.xml",
        },
        {
          rel: "preload",
          as: "image",
          href: "https://storage.googleapis.com/msgsndr/pLFrnXnIu7IgQ4WCaCfz/media/f5f1f2f1-bbe0-4ac9-8f9d-f59083df9317.webp",
          fetchPriority: "high",
        },
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            name: "Marjohn B. Robillo - Revenue Operations Architect",
            url: "https://marjohnrobillo.me",
            potentialAction: {
              "@type": "SearchAction",
              target: "https://marjohnrobillo.me/blog?q={search_term_string}",
              "query-input": "required name=search_term_string",
            },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Person",
            "@id": "https://marjohnrobillo.me/authors/marjohn-robillo#person",
            name: "Marjohn B. Robillo",
            jobTitle: "Revenue Operations Architect",
            url: "https://marjohnrobillo.me/authors/marjohn-robillo",
            knowsAbout: [
              "Revenue Operations",
              "GoHighLevel Automation",
              "HubSpot CRM Architecture",
              "n8n Workflow Development",
              "Zapier Integrations",
              "Make.com Automation",
              "Voice AI Automation",
              "Vapi AI Agents",
              "Agentic AI",
              "CRM Architecture",
              "Conversion Optimization",
            ],
            nationality: "Filipino",
            alumniOf: "Master of Public Administration (MPA)",
            sameAs: [
              "https://www.linkedin.com/in/marjohnrobillo-com/",
              "https://marjohnrobillo.com",
              "https://zappify.io",
              "https://www.youtube.com/channel/UCRFFj1RQDEiHv3LGiDz1srA",
              "https://www.crunchbase.com/person/marjohn-robillo",
              "https://www.wikidata.org/wiki/Q141613874",
            ],
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "Zappify.io",
            url: "https://zappify.io",
            founder: { "@type": "Person", name: "Marjohn B. Robillo" },
            description:
              "CRM automation and Revenue Operations engineering. 300+ CRM systems architected for international clients.",
            areaServed: ["United States", "Canada", "Australia"],
            knowsAbout: [
              "CRM",
              "n8n",
              "Voice AI",
              "Revenue Operations",
              "Automation",
            ],
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ProfessionalService",
            name: "Marjohn B. Robillo - Revenue Operations Architect",
            image:
              "https://assets.cdn.filesafe.space/pLFrnXnIu7IgQ4WCaCfz/media/6ac3f6752c1c51a2ac7081a0.png",
            url: "https://marjohnrobillo.me",
            priceRange: "$$$",
            areaServed: ["United States", "Canada", "Australia"],
            hasOfferCatalog: {
              "@type": "OfferCatalog",
              name: "Revenue Operations & Automation Services",
              itemListElement: [
                {
                  "@type": "Offer",
                  itemOffered: {
                    "@type": "Service",
                    name: "Revenue Operations Architecture",
                    serviceType: "Revenue Operations Architecture",
                  },
                },
                {
                  "@type": "Offer",
                  itemOffered: {
                    "@type": "Service",
                    name: "CRM Automation",
                    serviceType: "CRM Automation",
                  },
                },
                {
                  "@type": "Offer",
                  itemOffered: {
                    "@type": "Service",
                    name: "Voice AI Implementation",
                    serviceType: "Voice AI Implementation",
                  },
                },
                {
                  "@type": "Offer",
                  itemOffered: {
                    "@type": "Service",
                    name: "Workflow Orchestration",
                    serviceType: "Workflow Orchestration",
                  },
                },
              ],
            },
            founder: { "@type": "Person", name: "Marjohn B. Robillo" },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(videoObjectSchema()),
        },
      ],
    }),
    shellComponent: RootShell,
    component: RootComponent,
    notFoundComponent: RootNotFoundComponent,
    errorComponent: RootErrorComponent,
  },
);
